import { Injectable } from "@angular/core";
import { ApiService } from "../api/api.service";
import { AdministrativeConfigurations } from "src/app/models/Admin/AdministrativeConfigurations.model";
import { SessionService } from "../session/session.service";
import { AdministrativeOptionsEnum } from "src/app/Enums/AdministrativeOptionsEnum";
import { TempFileModel } from "src/app/models/Admin/TempFileModel";
import { DateTime } from "luxon";
import { Account } from "src/app/models/Admin/Account.model";
import { PdvInformation } from "src/app/models/Pdv/PdvInformation.model";
import { Partner } from "src/app/models/Admin/Partner.model";
import { unmaskCnpj } from "src/validators/cnpj/cnpj";
import { PdvUpdateModel } from "src/app/models/Pdv/PdvUpdate.model";
import { CreatePdvUpdateModel } from "src/app/models/Pdv/CreatePdvUpdate.model";
import { UploadPdvUpdateResponse } from "src/app/models/Pdv/UploadPdvUpdateResponse.model";
import { FlavorModel } from "src/app/models/Pdv/Flavor.model";
import { PdvFlavorModel } from "src/app/models/Pdv/PdvFlavor.model";
import { EditPdvUpdateModel } from "src/app/models/Pdv/EditPdvUpdate.model";
import { CreatePdvFlavorModel } from "src/app/models/Pdv/CreatePdvFlavor.model";
import { EditPdvFlavorModel } from "src/app/models/Pdv/EditPdvFlavor.model";

@Injectable({
    providedIn: 'root'
})
export class AdministrativeService {


    constructor(private api: ApiService, private sessionService: SessionService){
    }

    public async getAllAdministrativeConfigurations(): Promise<AdministrativeConfigurations[]> {
        try {
            const r = await this.api.get<AdministrativeConfigurations[]>("/admin/configuration");
            if (r.success)
                return r.data;

            console.error(r.message);
            return [];
        } catch (error) {
            throw (error);
        }
    }

    public async editLink(token: string, flag: string, link: string): Promise<boolean> {
        try {
            const r = await this.api.post<void>("/admin", {Token: token, Flag: flag, Link: link});
            return r.success;
        } catch (error) {
            throw (error);
        }
    }

    public async getConfigurationValue(idConfiguration: AdministrativeOptionsEnum): Promise<string> {
        try {
            const r = await this.api.get<string>("/admin/get-download-link", {idConfiguration: idConfiguration});
            if(r.success)
                return r.data

            console.error(r.message);
            return "";
        } catch (error) {
            throw(error);
        }
    }

    public async logUserDownload(idConfiguration: AdministrativeOptionsEnum): Promise<boolean> {
        try {
            let userDetails = this.sessionService.getDetails();
            const r = await this.api.post<void>("/admin/log-user", {UserId: userDetails.id, Date: new Date().toUTCString(), DownloadId: idConfiguration});
            return r.success
        } catch (error) {
            throw(error);
        }
    }

    public async getTempFiles(beginDate: Date, endDate: Date): Promise<TempFileModel[]> {
        const formattedBegin = DateTime.fromJSDate(beginDate).toISO();
        const formattedEnd = DateTime.fromJSDate(endDate).toISO();

        try {
            const r = await this.api.get<TempFileModel[]>("/admin/temp-file", {beginDate: formattedBegin, endDate: formattedEnd});
            if (r?.success)
                return r.data;

            console.error(r?.message);
            return [];
        } catch (error) {
            throw (error);
        }
    }

    public async getAccountTypes(): Promise<{ id:number, name: string}[]> {
        try {
            const r = await this.api.get<{id: number, name: string}[]>("/admin/account-types");
            if (r?.success)
                return r.data;

            console.error(r?.message);
            return [];
        } catch (error) {
            throw (error);
        }
    }

    public async getAccounts(page: number = 0, pageSize: number = 20,
        id: number | null = null,
        email: string | null = null,
        document: string | null = null
    ): Promise<{ totalItems: number, accounts: Account[] } | null> {
        try {
            if (document != null)
                document = unmaskCnpj(document);
            
            const r = await this.api.get<{totalItems: number, accounts: Account[]}>("/admin/accounts", { page, pageSize, id, email, document });
            
            if (r?.success)
                return r.data;

            console.error(r?.message);
            return null;
        } catch (error) {
            throw (error);
        }
    }

    public async getPartnerAccounts(page: number = 0, pageSize: number = 20,
        id: number | null = null,
        email: string | null = null,
        document: string | null = null
    ): Promise<{ totalItems: number, accounts: Account[] } | null> {
        try {
            if (document != null)
                document = unmaskCnpj(document);
            
            const r = await this.api.get<{totalItems: number, accounts: Account[]}>("/admin/accounts/partners", { page, pageSize, id, email, document });
            
            if (r?.success)
                return r.data;

            console.error(r?.message);
            return null;
        } catch (error) {
            throw (error);
        }
    }

    public async putAccountType(companyId: number, accountTypeId: number) {
        try {
            const r = await this.api.put<any>("/admin/accounts/"+companyId+"/account-type", { AccountTypeId: accountTypeId});
            if (r?.success)
                return true;
            
            console.error(r?.message);
            return null;
        } catch (error) {
            throw (error);
        }
    }

    public async putAccountActive(companyId: number, accountActive: boolean) {
        try {
            const r = await this.api.put<any>("/admin/accounts/"+companyId+"/account-active", { AccountActive: accountActive});
            if (r?.success)
                return true;

            console.error(r?.message);
            return null;
        } catch (error) {
            throw (error);
        }
    }

    public async putAccountEmail(companyId: number, email: string): Promise<{ success: boolean, message?: string }> {
        try {
            const r = await this.api.put<any>("/admin/accounts/"+companyId+"/email", { Email: email });
            if (r?.success)
                return { success: true };

            return { success: false, message: r?.message };
        } catch (error) {
            throw (error);
        }
    }

    public async putAccountDocument(companyId: number, document: string): Promise<{ success: boolean, message?: string }> {
        try {
            const r = await this.api.put<any>("/admin/accounts/"+companyId+"/document", { Document: unmaskCnpj(document) });
            if (r?.success)
                return { success: true };

            return { success: false, message: r?.message };
        } catch (error) {
            throw (error);
        }
    }

    public async getPdvsInformationsByCompanyId(companyId: number): Promise<any[]> {
        try {
            const r = await this.api.get<any[]>(`/v1/pdv/${companyId}/information`);
            if (r?.success) {
                return r.data;
            }
            return [];
        } catch (error) {
            throw error;
        }
    }

    public async getAllPartners(
    page: number = 0,
    pageSize: number = 20,
    id: number | null = null,
    email: string | null = null,
    companyName: string | null = null,
    trialPeriodConfig: number | null = null
    ): Promise<{ totalItems: number, accounts: Partner[] } | null> {
        try {
            const params: any = { page, pageSize };

            if (id !== null) params.id = id;
            if (email) params.email = email;
            if (companyName) params.companyName = companyName;
            if (trialPeriodConfig !== null) params.trialPeriodConfig = trialPeriodConfig;

            const r = await this.api.get<{ totalItems: number, accounts: Partner[] }>("/admin/accounts/partners", params);
            if (r?.success)
            return r.data;

            console.error(r?.message);
            return null;
        } catch (error) {
            throw error;
        }
    }


    public async updateTrialPeriod(partnerId: number, trialPeriod: number): Promise<boolean> {
        try {
            const r = await this.api.patch<any>(`/admin/accounts/partner/${partnerId}/trial-period`, { trialPeriod });
            if (r?.success)
                return true;

            console.error(r?.message);
            return false;
        } catch (error) {
            throw error;
        }
    }

    public async uploadPdvUpdateFile(file: File): Promise<UploadPdvUpdateResponse> {
        try {
            const form = new FormData();
            form.append("file", file);

            const r = await this.api.post<UploadPdvUpdateResponse>("/admin/pdv-updates-file", form);

            if (r?.success) {return r.data;}

            throw new Error(r?.message ?? "Upload failed");
        } catch (error) {
            throw error;
        }
    }

    public async createPdvUpdate(payload: CreatePdvUpdateModel): Promise<boolean> {
        try {
            const r = await this.api.post("/admin/pdv-updates", payload);
            return r?.success ?? false;
        } catch (error) {
            throw error;
        }
    }

    public async deactivatePdvUpdate(id: number): Promise<boolean> {
        try {
            const r = await this.api.put(`/admin/pdv-updates/${id}/deactivate`, null);
            return r?.success ?? false;
        } catch (error) {
            throw error;
        }
    }

    public async createFlavor(payload: CreatePdvFlavorModel): Promise<boolean> {
        try {
            const r = await this.api.post('/admin/pdv-flavors', payload);
            return r?.success ?? false;
        } catch (error) {
            throw error;
        }
    }

    public async editFlavor(payload: EditPdvFlavorModel): Promise<boolean> {
        try {
            const r = await this.api.put(
            `/admin/pdv-flavors/${payload.id}`,
            payload
            );

            return r?.success ?? false;
        } catch (error) {
            throw error;
        }
    }

    public async ToggleActive(id: number): Promise<boolean> {
        try {
            const r = await this.api.patch(`/admin/pdv-flavors/${id}/toggle-active`, {});
            return r?.success ?? false;
        } catch (error) {
            throw error;
        }
    }

    public async getActiveFlavors(): Promise<FlavorModel[]> {
        try {
            const r = await this.api.get<FlavorModel[]>("/admin/pdv-flavors/active");
            if (r?.success) {return r.data;}
            return [];
        } catch (error) {
            throw error;
        }
    }

    public async getFlavors(): Promise<PdvFlavorModel[]> {
        try {
            const r = await this.api.get<PdvFlavorModel[]>("/admin/pdv-flavors");
            if (r?.success) {return r.data;}
            return [];
        } catch (error) {
            throw error;
        }
    }

    public async getPdvUpdates(): Promise<PdvUpdateModel[]> {
        try {
            const r = await this.api.get<PdvUpdateModel[]>("/admin/pdv-updates");
            if (r?.success) {return r.data;}
            return [];
        } catch (error) {
            throw error;
        }
    }
}